/**
 * United States to Australian spelling, for medicolegal report text.
 *
 * Word lists, not regular expressions. A rule like `hem` becomes `haem` would
 * turn hemiplegia into haemiplegia and hemisphere into haemisphere, which is
 * exactly the sort of error that ends up in a report sent to an insurer. Every
 * entry below is a whole word that has been checked against its false friends.
 *
 * Inflections are generated rather than typed out: see `expand`.
 */
/**
 * The -ize / -ization family. Australian usage is -ise / -isation throughout.
 *
 * Words where the z is not a suffix (capsize, size, prize, seize, resize,
 * assize, maize) are absent by design, and `SPELLING_EXCLUDE` below stops any
 * future generated rule reaching them.
 */
const ISE_STEMS = [
    'apolog', 'author', 'categor', 'central', 'character', 'civil', 'colon',
    'computer', 'critic', 'crystall', 'custom', 'decentral', 'demoral',
    'desensit', 'destabil', 'digit', 'emphas', 'energ', 'equal', 'fertil',
    'final', 'formal', 'general', 'harmon', 'hospital', 'human', 'immobil',
    'immun', 'individual', 'industrial', 'initial', 'internal', 'ion', 'item',
    'jeopard', 'legal', 'liberal', 'local', 'material', 'maxim', 'memor',
    'minim', 'mobil', 'modern', 'moral', 'national', 'neutral', 'normal',
    'optim', 'organ', 'penal', 'personal', 'polar', 'popular', 'priorit',
    'privat', 'professional', 'public', 'random', 'rational', 'real', 'recogn',
    'regular', 'reorgan', 'revolution', 'sensit', 'special', 'stabil',
    'standard', 'steril', 'summar', 'sympath', 'synchron', 'theor', 'util',
    'vapor', 'visual', 'vocal',
];
/** Verbs in -yse. Australian never uses -yze. */
const YSE_STEMS = ['anal', 'catal', 'dial', 'electrol', 'hydrol', 'paral'];
/**
 * ae and oe digraphs. Medical vocabulary is where this matters most, and it is
 * also where a naive rule does the most damage, so each is listed.
 *
 * `fetal` and `fetus` are deliberately absent. Australian medical style has
 * moved to the e spelling, and the RACGP, RANZCOG and the AMA Guides all use
 * it. They are flagged for the author rather than rewritten.
 */
const DIGRAPH = [
    { us: 'anemia', au: 'anaemia' },
    { us: 'anemic', au: 'anaemic' },
    { us: 'anesthesia', au: 'anaesthesia' },
    { us: 'anesthetic', au: 'anaesthetic', inflect: true },
    { us: 'anesthetist', au: 'anaesthetist' },
    { us: 'anesthesiologist', au: 'anaesthetist', confidence: 'likely', note: 'Australian title is anaesthetist' },
    { us: 'anesthetize', au: 'anaesthetise', inflect: true },
    { us: 'apnea', au: 'apnoea' },
    { us: 'apneic', au: 'apnoeic' },
    { us: 'celiac', au: 'coeliac' },
    { us: 'cesarean', au: 'caesarean' },
    { us: 'cesarian', au: 'caesarean' },
    { us: 'diarrhea', au: 'diarrhoea' },
    { us: 'dyspnea', au: 'dyspnoea' },
    { us: 'dyspneic', au: 'dyspnoeic' },
    { us: 'edema', au: 'oedema' },
    { us: 'edematous', au: 'oedematous' },
    { us: 'esophageal', au: 'oesophageal' },
    { us: 'esophagus', au: 'oesophagus' },
    { us: 'estrogen', au: 'oestrogen' },
    { us: 'etiology', au: 'aetiology' },
    { us: 'etiological', au: 'aetiological' },
    { us: 'gynecologic', au: 'gynaecologic' },
    { us: 'gynecological', au: 'gynaecological' },
    { us: 'gynecologist', au: 'gynaecologist' },
    { us: 'gynecology', au: 'gynaecology' },
    { us: 'hemarthrosis', au: 'haemarthrosis' },
    { us: 'hematocrit', au: 'haematocrit' },
    { us: 'hematologic', au: 'haematologic' },
    { us: 'hematological', au: 'haematological' },
    { us: 'hematologist', au: 'haematologist' },
    { us: 'hematology', au: 'haematology' },
    { us: 'hematoma', au: 'haematoma' },
    { us: 'hematomas', au: 'haematomas' },
    { us: 'hematuria', au: 'haematuria' },
    { us: 'hemoglobin', au: 'haemoglobin' },
    { us: 'hemophilia', au: 'haemophilia' },
    { us: 'hemorrhage', au: 'haemorrhage', inflect: true },
    { us: 'hemorrhagic', au: 'haemorrhagic' },
    { us: 'hemorrhoid', au: 'haemorrhoid', inflect: true },
    { us: 'hemostasis', au: 'haemostasis' },
    { us: 'hemothorax', au: 'haemothorax' },
    { us: 'ischemia', au: 'ischaemia' },
    { us: 'ischemic', au: 'ischaemic' },
    { us: 'leukemia', au: 'leukaemia' },
    { us: 'orthopedic', au: 'orthopaedic' },
    { us: 'orthopedics', au: 'orthopaedics' },
    { us: 'orthopedist', au: 'orthopaedic surgeon', confidence: 'likely' },
    { us: 'orthopnea', au: 'orthopnoea' },
    { us: 'pediatric', au: 'paediatric' },
    { us: 'pediatrician', au: 'paediatrician' },
    { us: 'pediatrics', au: 'paediatrics' },
    { us: 'tachypnea', au: 'tachypnoea' },
    { us: 'toxemia', au: 'toxaemia' },
    { us: 'uremia', au: 'uraemia' },
    { us: 'septicemia', au: 'septicaemia' },
    { us: 'bacteremia', au: 'bacteraemia' },
    { us: 'hypoxemia', au: 'hypoxaemia' },
    { us: 'hypercapnea', au: 'hypercapnoea' },
    { us: 'paresthesia', au: 'paraesthesia' },
    { us: 'paresthesias', au: 'paraesthesiae' },
    { us: 'dysesthesia', au: 'dysaesthesia' },
    { us: 'hyperesthesia', au: 'hyperaesthesia' },
    { us: 'anesthesias', au: 'anaesthesiae' },
    { us: 'orthoses', au: 'orthoses' },
    { us: 'kinesthetic', au: 'kinaesthetic' },
    { us: 'edemas', au: 'oedemas' },
];
/** -our. `humerus` and `humeral` are bones and are untouched. */
const OUR = [
    { us: 'behavior', au: 'behaviour', inflect: true },
    { us: 'behavioral', au: 'behavioural' },
    { us: 'behaviorally', au: 'behaviourally' },
    { us: 'color', au: 'colour', inflect: true },
    { us: 'discolor', au: 'discolour', inflect: true },
    { us: 'discoloration', au: 'discolouration' },
    { us: 'endeavor', au: 'endeavour', inflect: true },
    { us: 'favor', au: 'favour', inflect: true },
    { us: 'favorable', au: 'favourable' },
    { us: 'favorably', au: 'favourably' },
    { us: 'unfavorable', au: 'unfavourable' },
    { us: 'harbor', au: 'harbour', inflect: true },
    { us: 'honor', au: 'honour', inflect: true },
    { us: 'humor', au: 'humour' },
    { us: 'labor', au: 'labour', inflect: true },
    { us: 'neighbor', au: 'neighbour', inflect: true },
    { us: 'neighborhood', au: 'neighbourhood' },
    { us: 'odor', au: 'odour' },
    { us: 'rumor', au: 'rumour' },
    { us: 'savior', au: 'saviour' },
    { us: 'tumor', au: 'tumour', inflect: true },
    { us: 'vapor', au: 'vapour' },
    { us: 'vigor', au: 'vigour' },
    { us: 'vigorous', au: 'vigorous' },
];
/**
 * -re. Bare `meter` is excluded: a peak flow meter is a device, not a length,
 * and the two are indistinguishable without context. It is flagged instead.
 */
const RE = [
    { us: 'caliber', au: 'calibre' },
    { us: 'center', au: 'centre', inflect: true },
    { us: 'centimeter', au: 'centimetre', inflect: true },
    { us: 'fiber', au: 'fibre', inflect: true },
    { us: 'fibrous', au: 'fibrous' },
    { us: 'kilometer', au: 'kilometre', inflect: true },
    { us: 'liter', au: 'litre', inflect: true },
    { us: 'luster', au: 'lustre' },
    { us: 'maneuver', au: 'manoeuvre', inflect: true },
    { us: 'micrometer', au: 'micrometre', inflect: true },
    { us: 'milliliter', au: 'millilitre', inflect: true },
    { us: 'millimeter', au: 'millimetre', inflect: true },
    { us: 'somber', au: 'sombre' },
    { us: 'specter', au: 'spectre' },
    { us: 'theater', au: 'theatre', inflect: true },
];
/** Doubled l before a suffix. */
const DOUBLE_L = [
    { us: 'canceled', au: 'cancelled' },
    { us: 'canceling', au: 'cancelling' },
    { us: 'cancelation', au: 'cancellation' },
    { us: 'channeled', au: 'channelled' },
    { us: 'counseled', au: 'counselled' },
    { us: 'counseling', au: 'counselling' },
    { us: 'counselor', au: 'counsellor', inflect: true },
    { us: 'dialed', au: 'dialled' },
    { us: 'equaled', au: 'equalled' },
    { us: 'fueled', au: 'fuelled' },
    { us: 'fueling', au: 'fuelling' },
    { us: 'labeled', au: 'labelled' },
    { us: 'labeling', au: 'labelling' },
    { us: 'marvelous', au: 'marvellous' },
    { us: 'modeled', au: 'modelled' },
    { us: 'modeling', au: 'modelling' },
    { us: 'signaled', au: 'signalled' },
    { us: 'signaling', au: 'signalling' },
    { us: 'totaled', au: 'totalled' },
    { us: 'traveled', au: 'travelled' },
    { us: 'traveler', au: 'traveller', inflect: true },
    { us: 'traveling', au: 'travelling' },
    { us: 'tunneled', au: 'tunnelled' },
];
/** Single l where the United States doubles it. */
const SINGLE_L = [
    { us: 'distill', au: 'distil' },
    { us: 'enroll', au: 'enrol' },
    { us: 'enrolled', au: 'enrolled' },
    { us: 'enrollment', au: 'enrolment' },
    { us: 'fulfill', au: 'fulfil' },
    { us: 'fulfillment', au: 'fulfilment' },
    { us: 'installment', au: 'instalment', inflect: true },
    { us: 'skillful', au: 'skilful' },
    { us: 'skillfully', au: 'skilfully' },
    { us: 'willful', au: 'wilful' },
    { us: 'willfully', au: 'wilfully' },
];
/**
 * -ce and -se. `license` and `practice` are both correct in Australian English
 * in one of their two parts of speech, so those are handled contextually in
 * `contextualSpelling` rather than by a flat map.
 */
const CE_SE = [
    { us: 'defense', au: 'defence', inflect: true },
    { us: 'defenseless', au: 'defenceless' },
    { us: 'offense', au: 'offence', inflect: true },
    { us: 'pretense', au: 'pretence' },
];
/** Everything else worth carrying. */
const MISC = [
    { us: 'aging', au: 'ageing' },
    { us: 'aluminum', au: 'aluminium' },
    { us: 'artifact', au: 'artefact', inflect: true },
    { us: 'ax', au: 'axe' },
    { us: 'catalog', au: 'catalogue', inflect: true },
    { us: 'cozy', au: 'cosy' },
    { us: 'defense', au: 'defence' },
    { us: 'dialog', au: 'dialogue' },
    { us: 'disk', au: 'disc', inflect: true, note: 'intervertebral disc' },
    { us: 'gray', au: 'grey', inflect: true },
    { us: 'jewelry', au: 'jewellery' },
    { us: 'judgement', au: 'judgment', note: 'Australian legal usage drops the e' },
    { us: 'mold', au: 'mould', inflect: true },
    { us: 'mom', au: 'mum' },
    { us: 'plow', au: 'plough', inflect: true },
    { us: 'practicing', au: 'practising' },
    { us: 'practiced', au: 'practised' },
    { us: 'program', au: 'program', note: 'Australian usage keeps program' },
    { us: 'sizeable', au: 'sizeable' },
    { us: 'smolder', au: 'smoulder', inflect: true },
    { us: 'sulfate', au: 'sulfate' },
    { us: 'thru', au: 'through' },
    { us: 'toward', au: 'towards' },
    { us: 'whiskey', au: 'whisky' },
    { us: 'gotten', au: 'got' },
];
/** Words that must never be rewritten, whatever a generated rule suggests. */
export const SPELLING_EXCLUDE = new Set([
    'size', 'sizes', 'sized', 'sizing', 'resize', 'resized', 'capsize', 'capsized',
    'prize', 'prizes', 'seize', 'seized', 'seizes', 'seizure', 'seizures', 'assize',
    'maize', 'baptize', 'bronze',
    'hemiplegia', 'hemiplegic', 'hemisphere', 'hemispheric', 'hemithorax',
    'hemianopia', 'hemianopsia', 'hemicolectomy', 'hemiarthroplasty',
    'hemodynamic', 'hemodynamics',
    'humerus', 'humeral', 'humeri',
    'meter', 'meters',
    'fetal', 'fetus', 'fetuses',
    'sulfur', 'sulfide', 'sulfasalazine',
    'estrogenic',
    'program', 'programs', 'programmed', 'programming',
    'check', 'checks', 'checked', 'checking', 'curb', 'curbs', 'curbed',
    'draft', 'drafts', 'drafted', 'drafting', 'story', 'stories',
    'burned', 'learned', 'spelled',
    'judgment', 'judgments',
    'practice', 'practices', 'license', 'licensed', 'licensing',
]);
/**
 * Words we notice but will not rewrite, with the reason. These surface in the
 * author's review panel so a human can decide.
 */
export const SPELLING_FLAGS = [
    // Homophone pairs where both spellings are correct Australian English in one
    // sense and wrong in the other. Rewriting these was worse than leaving them:
    // a doctor typing "Check the L4/5 finding before signing" was getting
    // "Cheque the L4/5 finding", which is a defect introduced by the corrector
    // rather than caught by it. Context is what decides, and this engine does not
    // have it, so it says what it noticed and stops there.
    { word: 'check', reason: 'A payment is a cheque. Everything else is a check.' },
    { word: 'curb', reason: 'The edge of a road is a kerb. To restrain is to curb.' },
    { word: 'draft', reason: 'A current of air is a draught. A document is a draft.' },
    { word: 'story', reason: 'A floor of a building is a storey. An account is a story.' },
    { word: 'burned', reason: 'Australian usage prefers burnt, but burned is not wrong.' },
    { word: 'learned', reason: 'Learnt as a verb, learned as an adjective. Both appear.' },
    { word: 'licence', reason: 'Licence is the noun, license the verb. Check which is meant.' },
    { word: 'license', reason: 'Licence is the noun, license the verb. Check which is meant.' },
    { word: 'practice', reason: 'Practice is the noun, practise the verb. Check which is meant.' },
    { word: 'fetal', reason: 'Australian medical style now prefers fetal over foetal. Left as written.' },
    { word: 'fetus', reason: 'Australian medical style now prefers fetus over foetus. Left as written.' },
    { word: 'meter', reason: 'Ambiguous. A peak flow meter is a device, a length is a metre.' },
    { word: 'meters', reason: 'Ambiguous. Devices are meters, lengths are metres.' },
    { word: 'attorney', reason: 'Australian usage is solicitor, barrister or legal representative.' },
    { word: 'plaintiff', reason: 'Correct in Western Australian civil practice. No change needed.' },
    { word: 'sulfur', reason: 'Australian chemical nomenclature keeps sulfur.' },
];
function suffixes(us, au) {
    const out = [[us, au]];
    const usEndsE = us.endsWith('e');
    const auEndsE = au.endsWith('e');
    const usStem = usEndsE ? us.slice(0, -1) : us;
    const auStem = auEndsE ? au.slice(0, -1) : au;
    out.push([us + 's', au + 's']);
    out.push([usEndsE ? us + 'd' : us + 'ed', auEndsE ? au + 'd' : au + 'ed']);
    out.push([usStem + 'ing', auStem + 'ing']);
    out.push([usStem + 'ed', auStem + 'ed']);
    out.push([us + 'ly', au + 'ly']);
    return out;
}
/** Build the flat lookup map, lowercase key to lowercase Australian form. */
function build() {
    const map = new Map();
    const add = (us, au, base) => {
        const key = us.toLowerCase();
        if (SPELLING_EXCLUDE.has(key))
            return;
        if (key === au.toLowerCase())
            return;
        if (map.has(key))
            return;
        map.set(key, { ...base, us: key, au });
    };
    for (const stem of ISE_STEMS) {
        for (const [usSuffix, auSuffix] of [
            ['ize', 'ise'], ['izes', 'ises'], ['ized', 'ised'], ['izing', 'ising'],
            ['ization', 'isation'], ['izations', 'isations'], ['izer', 'iser'],
            ['izers', 'isers'], ['izable', 'isable'],
        ]) {
            add(stem + usSuffix, stem + auSuffix, { us: '', au: '', confidence: 'certain' });
        }
    }
    for (const stem of YSE_STEMS) {
        if (stem.endsWith('yz') || stem.endsWith('y'))
            continue;
        for (const [usSuffix, auSuffix] of [
            ['yze', 'yse'], ['yzes', 'yses'], ['yzed', 'ysed'], ['yzing', 'ysing'],
            ['yzer', 'yser'],
        ]) {
            add(stem + usSuffix, stem + auSuffix, { us: '', au: '', confidence: 'certain' });
        }
    }
    for (const group of [DIGRAPH, OUR, RE, DOUBLE_L, SINGLE_L, CE_SE, MISC]) {
        for (const entry of group) {
            const pairs = entry.inflect ? suffixes(entry.us, entry.au) : [[entry.us, entry.au]];
            for (const [us, au] of pairs)
                add(us, au, entry);
        }
    }
    return map;
}
export const SPELLING_MAP = build();
/** Look a single lowercase word up. Returns undefined when nothing applies. */
export function lookupSpelling(word) {
    const key = word.toLowerCase();
    if (SPELLING_EXCLUDE.has(key))
        return undefined;
    const hit = SPELLING_MAP.get(key);
    if (!hit)
        return undefined;
    return { au: hit.au, confidence: hit.confidence ?? 'certain', note: hit.note };
}
/**
 * Match the source word's capitalisation onto the replacement.
 * OEDEMA, Oedema and oedema all come back looking like they were typed that way.
 */
export function matchCase(source, replacement) {
    if (source === source.toUpperCase() && /[A-Z]{2,}/.test(source))
        return replacement.toUpperCase();
    if (source[0] && source[0] === source[0].toUpperCase()) {
        return replacement.charAt(0).toUpperCase() + replacement.slice(1);
    }
    return replacement;
}
//# sourceMappingURL=spelling.js.map